import { useCallback, useEffect, useState } from "react";
import { dashFetch } from "@/api/client";
import { Card, Toolbar } from "@/components/Card";
import type { GlobalSettings } from "@/types/account";

function isMaskedSecret(v: string): boolean {
  const s = v.trim();
  return !s || s.startsWith("••");
}

export default function GlobalSettingsPanel() {
  const [loaded, setLoaded] = useState(false);
  const [gsMsg, setGsMsg] = useState("");

  const [botUaRegex, setBotUaRegex] = useState("");
  const [asstKey, setAsstKey] = useState("");
  const [asstModel, setAsstModel] = useState("gpt-4o-mini");
  const [asstEnabled, setAsstEnabled] = useState(true);

  const [slackClientId, setSlackClientId] = useState("");
  const [slackClientSecret, setSlackClientSecret] = useState("");
  const [slackSigningSecret, setSlackSigningSecret] = useState("");
  const [slackFromEnv, setSlackFromEnv] = useState(false);
  const [slackConfigured, setSlackConfigured] = useState(false);

  const [mailgunEnabled, setMailgunEnabled] = useState(false);
  const [mailgunKey, setMailgunKey] = useState("");
  const [mailgunRegion, setMailgunRegion] = useState<"us" | "eu">("us");
  const [mailgunDomain, setMailgunDomain] = useState("app.sitewrap.io");
  const [mailgunFromName, setMailgunFromName] = useState("Sitewrap");
  const [mailgunFromEmail, setMailgunFromEmail] = useState("noreply@app.sitewrap.io");
  const [mailgunReplyTo, setMailgunReplyTo] = useState("");
  const [mailgunFromEnv, setMailgunFromEnv] = useState(false);
  const [mailgunConfigured, setMailgunConfigured] = useState(false);

  const [pendingVerificationDays, setPendingVerificationDays] = useState(7);
  const [originHealthIntervalSec, setOriginHealthIntervalSec] = useState(60);

  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg, setTestMsg] = useState("");

  const applySettings = useCallback((gs: GlobalSettings) => {
    setBotUaRegex(gs.botDetection?.userAgentRegex || "");
    const asst = gs.assistant || {};
    setAsstKey(asst.hasApiKey ? String(asst.openaiApiKey || "••••") : "");
    setAsstModel(String(asst.model || "gpt-4o-mini"));
    setAsstEnabled(asst.enabled !== false);

    const slack = gs.slack || {};
    setSlackFromEnv(slack.source === "env");
    setSlackConfigured(Boolean(slack.configured));
    setSlackClientId(String(slack.clientId || ""));
    setSlackClientSecret(slack.hasClientSecret ? String(slack.clientSecret || "••••") : "");
    setSlackSigningSecret(slack.hasSigningSecret ? String(slack.signingSecret || "••••") : "");

    const mg = gs.mailgun || {};
    setMailgunFromEnv(mg.source === "env");
    setMailgunConfigured(Boolean(mg.configured));
    setMailgunEnabled(mg.enabled === true);
    setMailgunKey(mg.hasApiKey ? String(mg.apiKey || "••••") : "");
    setMailgunRegion(mg.region === "eu" ? "eu" : "us");
    setMailgunDomain(String(mg.sendingDomain || "app.sitewrap.io"));
    setMailgunFromName(String(mg.fromName || "Sitewrap"));
    setMailgunFromEmail(String(mg.fromEmail || "noreply@app.sitewrap.io"));
    setMailgunReplyTo(String(mg.replyTo || ""));

    setPendingVerificationDays(Number(gs.provisioning?.pendingVerificationDays) || 7);
    setOriginHealthIntervalSec(Number(gs.originHealth?.checkIntervalSeconds) || 60);
  }, []);

  const loadGlobalSettings = useCallback(async () => {
    const r = await dashFetch<{ ok?: boolean; settings?: GlobalSettings }>("/dashboard/api/account/global-settings", {
      cache: "no-store"
    });
    if (!r?.ok) throw new Error("load failed");
    applySettings(r.settings || {});
    setLoaded(true);
  }, [applySettings]);

  useEffect(() => {
    void loadGlobalSettings().catch((e) => {
      setGsMsg(`Failed to load: ${String(e instanceof Error ? e.message : e)}`);
    });
  }, [loadGlobalSettings]);

  const mailgunPayload = () => ({
    enabled: mailgunEnabled,
    region: mailgunRegion,
    sendingDomain: mailgunDomain.trim(),
    fromEmail: mailgunFromEmail.trim(),
    fromName: mailgunFromName.trim(),
    replyTo: mailgunReplyTo.trim(),
    ...(mailgunKey && !isMaskedSecret(mailgunKey) ? { apiKey: mailgunKey.trim() } : {})
  });

  const saveGlobalSettings = async () => {
    setGsMsg("Saving…");
    try {
      const payload: Record<string, unknown> = {
        botDetection: { userAgentRegex: botUaRegex },
        assistant: {
          model: asstModel,
          enabled: asstEnabled,
          ...(asstKey && !isMaskedSecret(asstKey) ? { openaiApiKey: asstKey } : {})
        },
        provisioning: {
          pendingVerificationDays: Math.max(1, Math.min(365, Math.floor(Number(pendingVerificationDays) || 7)))
        },
        originHealth: {
          checkIntervalSeconds: Math.max(15, Math.min(3600, Math.floor(Number(originHealthIntervalSec) || 60)))
        },
        ...(!slackFromEnv
          ? {
              slack: {
                clientId: slackClientId.trim(),
                ...(slackClientSecret && !isMaskedSecret(slackClientSecret) ? { clientSecret: slackClientSecret } : {}),
                ...(slackSigningSecret && !isMaskedSecret(slackSigningSecret) ? { signingSecret: slackSigningSecret } : {})
              }
            }
          : {}),
        ...(!mailgunFromEnv ? { mailgun: mailgunPayload() } : { mailgun: { enabled: mailgunEnabled, fromEmail: mailgunFromEmail.trim(), fromName: mailgunFromName.trim(), replyTo: mailgunReplyTo.trim(), sendingDomain: mailgunDomain.trim(), region: mailgunRegion } })
      };
      const r = await dashFetch<{ ok?: boolean; error?: string }>("/dashboard/api/account/global-settings", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!r?.ok) throw new Error(String(r.error || "save failed"));
      setGsMsg("Saved.");
      await loadGlobalSettings();
    } catch (e) {
      setGsMsg(`Save failed: ${String(e instanceof Error ? e.message : e)}`);
    }
  };

  const sendTestEmail = async () => {
    const to = testTo.trim();
    if (!to) {
      setTestMsg("Enter a recipient email address.");
      return;
    }
    setTestBusy(true);
    setTestMsg("Sending…");
    try {
      const r = await dashFetch<{ ok?: boolean; error?: string; message?: string; id?: string }>(
        "/dashboard/api/account/mailgun/test",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            to,
            config: {
              ...mailgunPayload(),
              enabled: true,
              ...(mailgunKey && !isMaskedSecret(mailgunKey) ? { apiKey: mailgunKey.trim() } : {})
            }
          })
        }
      );
      if (!r?.ok) {
        const detail = String(r.message || r.error || "send failed");
        throw new Error(detail);
      }
      setTestMsg(r.id ? `Test email queued (${r.id}).` : "Test email sent.");
    } catch (e) {
      setTestMsg(`Failed: ${String(e instanceof Error ? e.message : e)}`);
    } finally {
      setTestBusy(false);
    }
  };

  const fromPreview =
    mailgunFromName.trim() && mailgunFromEmail.trim()
      ? `${mailgunFromName.trim()} <${mailgunFromEmail.trim()}>`
      : mailgunFromEmail.trim() || "—";

  return (
    <Card title="Global settings">
      <p className="muted gsIntro">Fleet-wide defaults applied to every site on this proxy.</p>

      <section className="gsSection">
        <header className="gsSectionHead">
          <h3>Bot detection</h3>
          <p>User-Agent patterns matched before traffic is logged or billed as human.</p>
        </header>
        <div className="field">
          <label>User-Agent patterns (regex, one per line)</label>
          <textarea className="mono" spellCheck={false} rows={6} value={botUaRegex} onChange={(e) => setBotUaRegex(e.target.value)} />
        </div>
      </section>

      <section className="gsSection">
        <header className="gsSectionHead">
          <h3>Origin health checks</h3>
          <p>
            How often Sitewrap sends an HTTP <span className="mono">HEAD /</span> to each site origin for dashboard status and
            queue recovery (per domain, cached).
          </p>
        </header>
        <div className="field" style={{ maxWidth: 280 }}>
          <label>Check interval (seconds)</label>
          <input
            type="number"
            className="mono"
            min={15}
            max={3600}
            step={1}
            value={originHealthIntervalSec}
            onChange={(e) => setOriginHealthIntervalSec(Number(e.target.value))}
          />
          <p className="fieldHint">Default 60 (once per minute). Minimum 15, maximum 3600.</p>
        </div>
      </section>

      <section className="gsSection">
        <header className="gsSectionHead">
          <h3>ChatGPT assistant</h3>
          <p>OpenAI credentials for the dashboard assistant on all sites.</p>
        </header>
        <div className="row">
          <div className="field" style={{ flex: 1, minWidth: 260 }}>
            <label>OpenAI API key</label>
            <input type="password" className="mono" autoComplete="off" value={asstKey} onChange={(e) => setAsstKey(e.target.value)} placeholder="sk-…" />
          </div>
          <div className="field" style={{ width: 220 }}>
            <label>Model</label>
            <input type="text" className="mono" value={asstModel} onChange={(e) => setAsstModel(e.target.value)} />
          </div>
        </div>
        <label className="filterChip">
          <input type="checkbox" checked={asstEnabled} onChange={(e) => setAsstEnabled(e.target.checked)} /> Enable assistant chat
        </label>
      </section>

      <section className="gsSection">
        <header className="gsSectionHead">
          <h3>Slack integration</h3>
          {slackFromEnv ? (
            <p className="gsEnvNote">
              Using <code className="mono">SLACK_*</code> environment variables (overrides saved values).
            </p>
          ) : (
            <p>
              Fleet-wide Slack app credentials for OAuth and Events API.{" "}
              <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer">Create an app</a>
            </p>
          )}
        </header>
        <div className="row">
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label>Client ID</label>
            <input type="text" className="mono" autoComplete="off" value={slackClientId} onChange={(e) => setSlackClientId(e.target.value)} disabled={slackFromEnv} placeholder="1234567890.1234567890" />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label>Client secret</label>
            <input type="password" className="mono" autoComplete="off" value={slackClientSecret} onChange={(e) => setSlackClientSecret(e.target.value)} disabled={slackFromEnv} />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label>Signing secret</label>
            <input type="password" className="mono" autoComplete="off" value={slackSigningSecret} onChange={(e) => setSlackSigningSecret(e.target.value)} disabled={slackFromEnv} />
          </div>
        </div>
        {slackConfigured && !slackFromEnv ? <p className="ok gsStatus">Slack credentials saved.</p> : null}
      </section>

      <section className="gsSection gsSectionEmail">
        <header className="gsSectionHead">
          <h3>Email (Mailgun)</h3>
          <p>
            Outbound email via Mailgun using your <strong>@app.sitewrap.io</strong> domain. Add the Sending API key from Mailgun →
            Settings → API keys.
          </p>
        </header>

        {mailgunFromEnv ? (
          <p className="gsEnvNote">
            API key is loaded from <code className="mono">MAILGUN_API_KEY</code> or <code className="mono">MAILGUN_SENDING_API_KEY</code>.
            Sender fields below can still be saved.
          </p>
        ) : null}

        <label className="filterChip gsEnableRow">
          <input type="checkbox" checked={mailgunEnabled} onChange={(e) => setMailgunEnabled(e.target.checked)} />
          Enable outbound email
        </label>

        <div className="row">
          <div className="field" style={{ flex: 1, minWidth: 280 }}>
            <label>Mailgun Sending API key</label>
            <input
              type="password"
              className="mono"
              autoComplete="off"
              value={mailgunKey}
              onChange={(e) => setMailgunKey(e.target.value)}
              disabled={mailgunFromEnv}
              placeholder="key-…"
            />
            <p className="fieldHint">Private key with “Send” permission only — not the account password.</p>
          </div>
          <div className="field" style={{ width: 160 }}>
            <label>API region</label>
            <select value={mailgunRegion} onChange={(e) => setMailgunRegion(e.target.value === "eu" ? "eu" : "us")}>
              <option value="us">US (api.mailgun.net)</option>
              <option value="eu">EU (api.eu.mailgun.net)</option>
            </select>
          </div>
        </div>

        <div className="row">
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label>Sending domain</label>
            <input type="text" className="mono" value={mailgunDomain} onChange={(e) => setMailgunDomain(e.target.value)} placeholder="app.sitewrap.io" />
            <p className="fieldHint">Must match the domain verified in Mailgun.</p>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>Sender name</label>
            <input type="text" value={mailgunFromName} onChange={(e) => setMailgunFromName(e.target.value)} placeholder="Sitewrap" />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 240 }}>
            <label>Sender email</label>
            <input type="email" className="mono" value={mailgunFromEmail} onChange={(e) => setMailgunFromEmail(e.target.value)} placeholder="noreply@app.sitewrap.io" />
          </div>
        </div>

        <div className="field" style={{ maxWidth: 360 }}>
          <label>Reply-To <span className="muted">(optional)</span></label>
          <input type="email" className="mono" value={mailgunReplyTo} onChange={(e) => setMailgunReplyTo(e.target.value)} placeholder="support@app.sitewrap.io" />
        </div>

        <div className="gsPreview">
          <span className="muted">Messages will appear as</span>
          <code className="mono">{fromPreview}</code>
          {mailgunConfigured ? <span className="ok gsBadge">Ready</span> : mailgunEnabled ? <span className="gsBadge muted">Incomplete</span> : null}
        </div>

        <div className="gsTestBox">
          <div className="gsTestHead">
            <strong>Send test email</strong>
            <span className="muted">Uses the values above (save optional — unsaved API key is used if entered).</span>
          </div>
          <div className="row gsTestRow">
            <div className="field" style={{ flex: 1, minWidth: 260 }}>
              <label>Recipient</label>
              <input
                type="email"
                className="mono"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@example.com"
                disabled={testBusy}
              />
            </div>
            <button type="button" className="btn" disabled={testBusy || !loaded} onClick={() => void sendTestEmail()}>
              {testBusy ? "Sending…" : "Send test"}
            </button>
          </div>
          {testMsg ? <p className={testMsg.startsWith("Failed") ? "err" : "ok"} style={{ fontSize: 12, marginTop: 8 }}>{testMsg}</p> : null}
        </div>
      </section>

      <section className="gsSection">
        <header className="gsSectionHead">
          <h3>Site provisioning</h3>
          <p>Automatic cleanup for sites that never complete verification.</p>
        </header>
        <div className="field" style={{ maxWidth: 280 }}>
          <label>Pending verification expiry (days)</label>
          <input
            type="number"
            min={1}
            max={365}
            className="mono"
            value={pendingVerificationDays}
            onChange={(e) => setPendingVerificationDays(Number(e.target.value))}
          />
        </div>
      </section>

      <Toolbar>
        <span className="muted mono" style={{ fontSize: 11 }}>{gsMsg}</span>
        <button type="button" className="btn primary" disabled={!loaded} onClick={() => void saveGlobalSettings()}>
          Save global settings
        </button>
      </Toolbar>
    </Card>
  );
}
